import { ScreenReaderOnly, usePrevious } from '@op/opds.opux';
import { useTranslation } from 'react-i18next';
import { type CoverageProduct } from '../../../dto/marketableProduct';
import { useCartSummary } from '../../../state/selectors/coverageSelectors';
import { formatCentsToEuros } from '../../../utils';
import { formatCentsToEurosWithSeparators } from '../../../utils/currency-utils';

const ScreenReaderCartUpdates = () => {
  const { t } = useTranslation();
  const { selectedPremiums, monthlyTotal, annualTotal } = useCartSummary();

  const previousMonthlyTotal = usePrevious(monthlyTotal);
  const previousSelectedPremiums = usePrevious(selectedPremiums);

  const isMonthlyTotalChanged = previousMonthlyTotal !== monthlyTotal;
  const changedCoverageProduct = (
    Object.keys(selectedPremiums) as CoverageProduct[]
  ).find(
    (product) =>
      !Object.is(selectedPremiums[product], previousSelectedPremiums[product])
  );

  const monthlyTotalString = formatCentsToEuros(monthlyTotal);
  const annualTotalString = formatCentsToEurosWithSeparators(annualTotal);

  const totalUpdateText = isMonthlyTotalChanged
    ? `${t('common.priceTotal')}: ${monthlyTotalString} ${t('common.eurosPerMonth')}, ${annualTotalString} € ${t('common.perYear')}.`
    : null;

  const coverageName = changedCoverageProduct
    ? t(`coverageProduct.${changedCoverageProduct}.name`)
    : null;
  const coverageMonthlyTotal = changedCoverageProduct
    ? `${formatCentsToEurosWithSeparators(selectedPremiums?.[changedCoverageProduct]?.monthlyNetPremium.amountInCents ?? 0)} ${t('common.eurosPerMonth')}`
    : null;
  const coverageAnnualTotal =
    changedCoverageProduct &&
    `${formatCentsToEurosWithSeparators(selectedPremiums?.[changedCoverageProduct]?.annualNetPremium.amountInCents ?? 0)} € ${t('common.perYear')}.`;

  const coverageUpdateText = changedCoverageProduct
    ? `${coverageName}: ${coverageMonthlyTotal}, ${coverageAnnualTotal}`
    : null;

  return (
    <ScreenReaderOnly
      aria-live='polite'
      aria-atomic={true}
      tagName='div'
      data-testid='screen-reader-cart-updates'>
      {`${coverageUpdateText ?? ''} ${totalUpdateText ?? ''}`.trim()}
    </ScreenReaderOnly>
  );
};

export default ScreenReaderCartUpdates;
